import React, {Component, forwardRef, useState} from 'react'
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import PrimeDateRangeInput from "../../../../../components/date-input/PrimeDateRangeInput";
import HoverableDescriptionBox from "../../../../../components/hoverable-desc-box/HoverableDescriptionBox";
import "./PreApproval.css";
import {Col, Container, Row, Tab, Nav, Button, Dropdown} from "react-bootstrap";
import Select from "react-select";
import ReferralValidityTab from "./ReferralValidityTab";
import ReferralCliniciansTab from "./ReferralCliniciansTab";
import ReferralDatesTab from "./ReferralDatesTab";
import ReferralInsuranceTab from "./ReferralInsuranceTab";
import InsuranceReferralStatus from "../../../enums/InsuranceReferralStatus";
import DirectBillingReferralTypeEnum from "../../../enums/DirectBillingReferralTypeEnum";
import ReferralAppendixTab from "./ReferralAppendixTab";
import ReferralObsXMLsTab from "./ReferralObsXMLsTab";
import EnumDirectBillingType from "../../direct/EnumDirectBillingType";
import DateUtil from "../../../../../utils/DateUtil";
import RemotingService from "../../../../../services/remoting-service/RemotingService";
import NotificationService from "../../../../../services/notification-service/NotificationService";
import moment from "moment";
import CreateReferralModal from "./CreateReferralModal";
import ReferralItemModal from "./ReferralItemModal";
import PageTitle from "../../../../../layout/PageTitle";
import {Modal} from "../../../../../components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import InsuranceDocumentLegend from "../../InsuranceDocumentLegend";
import ReferralCycleEnum from "../../../enums/ReferralCycleEnum";
import IcdCodeSelector from "../../component/IcdCodeSelector";

export default class PreApproval extends Component {
    constructor(props) {
        super(props);

        this.referralItemModalRefs = {};
        this.state = {
            referrals: [],
            expandedReferrals: {},
            selectedReferrals: [],
            clinicOptions: [],
            selectedTabs: {},
            createReferralModalVisible: false,
            dateRange: [new Date(), new Date()],
            page: 1,
            rows: 10,
            totalCount: 0,
            totalPageCount: 0,
            filters: {
                referralType: '',
                modality: '',
                patientMrn: '',
                patientName: '',
                status: '',
                cycle: '',
            }
        }
    }

    componentDidMount() {
        this.retrieveReferrals();
        this.retrieveClinicOptions();
    }

    retrieveClinicOptions = () => {
        RemotingService.getRemoteCall('api/clinic/list', null, (result) => {
            const clinics = result.items;
            clinics.forEach(i => { i.key = "" + i.id; });
            this.setState({ clinicOptions: clinics });
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevProps.selected && this.props.selected) {
            this.retrieveReferrals()
        }
    }

    componentWillUnmount() {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
    }

    retrieveReferrals = () => {
        const { dateRange, page, rows, filters } = this.state;
        const [startDate, endDate] = dateRange || [new Date(), new Date()];

        // Only fetch if both dates are selected
        if (!startDate || !endDate) {
            return;
        }

        const params = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            page,
            size: rows,
        };
        // Only send non-empty filters so they combine with AND on the backend.
        if (filters.cycle) params.cycle = filters.cycle;
        if (filters.referralType) params.type = filters.referralType;
        if (filters.modality) params.modality = filters.modality;
        if (filters.status) params.status = filters.status;
        if (filters.patientMrn) params.mrn = filters.patientMrn;
        if (filters.patientName) params.name = filters.patientName;

        return RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/referral',
            params,
            (result) => {
                const items = result?.items || [];
                this.setIsReferralLocked(items)
                this.setState({
                    referrals: items,
                    totalCount: result?.totalCount || 0,
                    totalPageCount: result?.totalPageCount || 0,
                    page: result?.currentPage || page
                })
            })
    }

    setIsReferralLocked = (referrals) => {
        const lockedStatuses = [
            InsuranceReferralStatus.APPROVED.key,
            InsuranceReferralStatus.COMPLETED.key,
            InsuranceReferralStatus.CANCELLED.key,
            InsuranceReferralStatus.EXPIRED.key,
        ];
        
        referrals.forEach(referral => {
            referral.isLocked = lockedStatuses.includes(referral.status)
        })
    }


    validateReferralUpdate = (referral) => {
        const referralFields = [
            { field: 'validFrom',   label: 'Valid From' },
            { field: 'validUntil',  label: 'Valid Until' },
            { field: 'modality',  label: 'Modality' },
        ];

        const missing = referralFields
            .filter(({ field }) => {
                const v = referral[field];
                return v === null || v === undefined || v === '';
            })
            .map(({ label }) => label);

        if (missing.length > 0) {
            NotificationService.showNotification({
                severity: 'error',
                summary: 'Validation Error',
                detail: `The following fields are required: ${missing.join(', ')}`
            });
            return false;
        }
        return true;
    }

    updateReferral = (referral) => {
        // Fields are only required when moving to one of these statuses; for any
        // other target status (e.g. PENDING, REQUESTED) nothing is required.
        const statusesRequiringValidation = [
            InsuranceReferralStatus.APPROVED.key,
            InsuranceReferralStatus.COMPLETED.key,
            InsuranceReferralStatus.CANCELLED.key,
            InsuranceReferralStatus.EXPIRED.key,
            InsuranceReferralStatus.DENIED.key,
            InsuranceReferralStatus.EDIT.key,
        ];

        const diagnosisItems = referral.diagnosisGroup?.diagnosisItems || [];
        if (diagnosisItems.length > 0 && !diagnosisItems.some(i => i.level === 'PRINCIPAL')) {
            NotificationService.showNotification({ severity: 'error', summary: 'Validation Error', detail: 'A Principal diagnosis is required.' });
            return;
        }

        if (statusesRequiringValidation.includes(referral.status)) {
            if (!this.validateReferralUpdate(referral)) return;

            const hasItemIssues = referral.items?.some(item =>
                // Approved sessions must be entered; 0 means the item is not authorized,
                // so its co-pay fields are not required.
                item.noOfApprovedSessions == null ||
                (item.noOfApprovedSessions > 0 && (!item.coPayUnit || item.coPay == null))
            );

            if (hasItemIssues) {
                this.expandAndValidateItems(referral.id);
                return;
            }
        }

        const data = {
            status: referral.status,
            modality: referral.modality,
            referenceNo: referral.referenceNo,
            validFrom: referral.validFrom,
            validUntil: referral.validUntil,
            coPayMax: referral.coPayMax,
            clinicId: referral.clinic?.id,
            referringClinicianId: referral.referringClinician?.id,
            referringClinicianName: referral.referringClinicianName,
            referringClinicianLicenseNo: referral.referringClinicianLicenseNo,
            referringClinicName: referral.referringClinicName,
            referringClinicId: referral.referringClinicId,
            performingClinicianId: referral.performingClinician?.id,
            requestedDateTime: referral.requestedDateTime,
            responseReceivedDateTime: referral.responseReceivedDateTime,
            items: referral.items,
            diagnosisGroup: referral.diagnosisGroup,
            cycle: referral.cycle,
            cycleOtherDetail: referral.cycle === 'OTHER' ? referral.cycleOtherDetail : null,
            claimFormStatus: referral.claimFormStatus,
            medicalReportStatus: referral.medicalReportStatus,
        }

        RemotingService.putRemoteCall(
            'api/insurance/coordinator/v2/referral/' + referral.id,
            data,
            () => {
                this.retrieveReferrals()
            })
    }

    expandAndValidateItems = (referralId) => {
        const alreadyExpanded = !!this.state.expandedReferrals[referralId];
        if (alreadyExpanded) {
            const modal = this.referralItemModalRefs[referralId];
            if (modal) modal.validate();
        } else {
            this.setState(
                { expandedReferrals: { ...this.state.expandedReferrals, [referralId]: true } },
                () => {
                    const modal = this.referralItemModalRefs[referralId];
                    if (modal) modal.validate();
                }
            );
        }
    }

    handleEdit = (referral) => {
        RemotingService.getRemoteCall(
            'api/insurance/coordinator/v2/referral/' + referral.id + '/edit',
            {},
            () => {
                this.retrieveReferrals()
            }
        )
    }

    handleDateRangeChange = (dateRange) => {
        this.setState({ dateRange, page: 1 }, () => {
            // Only fetch when both start and end dates are selected
            if (dateRange && dateRange[0] && dateRange[1]) {
                this.retrieveReferrals();
            }
        });
    }

    handleReferralChange = (referralId, field, value) => {
        this.setState(prevState => ({
            referrals: prevState.referrals.map(r =>
                r.id === referralId
                    ? { ...r, [field]: value }
                    : r
            )
        }));
    }

    mrnTemplate = (referral) => (
        <div style={{fontSize: '13px', textAlign: 'center'}}>{referral.patient.fullMrn}</div>
    )

    patientTemplate = (referral) => (
        <div className="patient-name" style={{fontSize: '14px', textAlign: 'center'}}>
            {referral.patient.firstName +
                (referral.patient.middleName ? " " + referral.patient.middleName + " " : " ") +
                referral.patient.lastName}
        </div>
    )

    icdTemplate = (referral) => (
        <DiagnosisCell
            referral={referral}
            onDiagnosisChange={(items) =>
                this.handleReferralChange(referral.id, 'diagnosisGroup', {
                    ...referral.diagnosisGroup,
                    diagnosisItems: items
                })
            }
        />
    )

    referralTypeTemplate = (referral) => (
        <div className="referral-type-cell" style={{fontSize: '14px', textAlign: 'center'}}>
            {referral.type ? DirectBillingReferralTypeEnum[referral.type]?.name : ''}
        </div>
    )

    statusTemplate = (referral) => {
        const opts = InsuranceReferralStatus.toArray().map(item => (
            {
                value: item.key,
                label: item.name
            }
        ))

        const customStyles = {
            control: (base) => ({
                ...base,
                width: '120px',
                minHeight: '30px',
                height: '35px',
                fontSize: '12px',
                padding: '0px',
            }),
            valueContainer: (base) => ({
                ...base,
                height: '30px',
                padding: '0 8px'
            }),
            input: (base) => ({
                ...base,
                margin: '0px',
                padding: '0px'
            }),
            indicatorsContainer: (base) => ({
                ...base,
                height: '30px',
            }),
            option: (base) => ({
                ...base,
                fontSize: '12px',
                padding: '6px 8px'
            }),
        };
        
        // Find the current value in options based on referral.status
        const selectedOption = opts.find(option => option.value === referral.status) || null;
        const locked = referral.isLocked;

        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Select 
                    options={opts} 
                    styles={customStyles}
                    value={selectedOption}
                    isDisabled={locked}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(value) => {
                        this.handleReferralChange(referral.id, 'status', value.value)
                    }}
                />
            </div>
        )
    }

    clinicColumnTemplate = (referral) => {
        const opts = this.state.clinicOptions.map(c => ({ value: c.id, label: c.name }));
        const selectedOption = opts.find(o => o.value === referral.clinic?.id) || null;
        const locked = referral.isLocked;
        const customStyles = {
            container: (base) => ({ ...base, width: '100%' }),
            control: (base) => ({ ...base, width: '100%', minHeight: '28px', height: '28px', fontSize: '11px', padding: '0px' }),
            valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
            input: (base) => ({ ...base, margin: '0px', padding: '0px' }),
            indicatorsContainer: (base) => ({ ...base, height: '28px' }),
            dropdownIndicator: (base) => ({ ...base, padding: '0 2px' }),
            option: (base) => ({ ...base, fontSize: '11px', padding: '4px 6px' }),
        };
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <Select
                    options={opts}
                    styles={customStyles}
                    value={selectedOption}
                    isDisabled={locked}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(opt) => this.handleReferralChange(referral.id, 'clinic', { id: opt.value, name: opt.label })}
                />
            </div>
        );
    }

    visitDateTemplate = (referral) => {
        if (DirectBillingReferralTypeEnum[referral.type] === DirectBillingReferralTypeEnum.INTERNAL) {
            const startTime = referral.visit?.appointment?.startTime;
            return <div style={{ fontSize: '13px', textAlign: 'center' }}>
                {startTime ? DateUtil.formatDate(startTime, 'Do MMM YYYY') : ''}
            </div>;
        }
        return <div style={{ fontSize: '13px', textAlign: 'center' }}>N/A</div>;
    }

    modalityColumnTemplate = (referral) => {
        const opts = EnumDirectBillingType.radiologyExternalReferralType.map(item => ({ value: item.key, label: item.name }));
        const selectedOption = opts.find(o => o.value === referral.modality) || null;
        const locked = referral.isLocked;
        const customStyles = {
            control: (base) => ({ ...base, width: '130px', minHeight: '30px', height: '30px', fontSize: '12px', padding: '0px' }),
            valueContainer: (base) => ({ ...base, height: '30px', padding: '0 8px' }),
            input: (base) => ({ ...base, margin: '0px', padding: '0px' }),
            indicatorsContainer: (base) => ({ ...base, height: '30px' }),
            option: (base) => ({ ...base, fontSize: '12px', padding: '6px 8px' }),
        };
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Select
                    options={opts}
                    styles={customStyles}
                    value={selectedOption}
                    isDisabled={locked}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(opt) => this.handleReferralChange(referral.id, 'modality', opt.value)}
                />
            </div>
        );
    }

    cycleColumnTemplate = (referral) => {
        const opts = ReferralCycleEnum.toArray().map(item => ({ value: item.key, label: item.name }));
        const selectedOption = opts.find(o => o.value === referral.cycle) || null;
        const locked = referral.isLocked;
        const customStyles = {
            control: (base) => ({ ...base, width: '120px', minHeight: '26px', height: '26px', fontSize: '11px', padding: '0px' }),
            valueContainer: (base) => ({ ...base, height: '26px', padding: '0 6px' }),
            input: (base) => ({ ...base, margin: '0px', padding: '0px' }),
            indicatorsContainer: (base) => ({ ...base, height: '26px' }),
            option: (base) => ({ ...base, fontSize: '11px', padding: '4px 6px' }),
        };
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <Select
                    options={opts}
                    styles={customStyles}
                    value={selectedOption}
                    isDisabled={locked}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={(opt) => {
                        const newCycle = opt ? opt.value : null;
                        this.setState(prevState => ({
                            referrals: prevState.referrals.map(r =>
                                r.id === referral.id
                                    ? { ...r, cycle: newCycle, cycleOtherDetail: newCycle !== 'OTHER' ? null : r.cycleOtherDetail }
                                    : r
                            )
                        }));
                    }}
                />
                {referral.cycle === 'OTHER' && (
                    <input
                        type="text"
                        disabled={locked}
                        placeholder="Other detail..."
                        value={referral.cycleOtherDetail || ''}
                        onChange={(e) => this.handleReferralChange(referral.id, 'cycleOtherDetail', e.target.value)}
                        style={{ width: '120px', fontSize: '11px', padding: '2px 4px', height: '24px', border: '1px solid #ced4da', borderRadius: '4px' }}
                    />
                )}
            </div>
        );
    }

    detailsTemplate = (referral) => {
        const locked = referral.isLocked;
        return (
        <div>
            <Container>
                <Row>
                    <Col xs={12}>
                        <div>
                            <Tab.Container defaultActiveKey={'insurance'}
                                           id={`referral-tabs-${referral.id}`}
                                           className="ReferralTabs"
                                           onSelect={(key) => {
                                               this.setState({
                                                   selectedTabs: {
                                                       ...this.state.selectedTabs,
                                                       [referral.id]: key
                                                   }
                                               })
                                           }}>
                                <Row>
                                    <Col sm={12}>
                                        <Nav variant="tabs" className="flex-row" style={{paddingLeft: '10px', paddingRight: '10px'}}>
                                            <Nav.Item>
                                                <Nav.Link eventKey="insurance">Insurance</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="validity">Validity</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="clinicians">Referrers</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="dates">Dates</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="automation">Obs/XMLs</Nav.Link>
                                            </Nav.Item>
                                            <ReferralAppendixTab
                                                referral={referral}
                                                onNoteClose={this.retrieveReferrals}
                                                readOnly={locked}
                                            />
                                        </Nav>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={12}>
                                        <Tab.Content style={{paddingLeft: '20px', paddingRight: '20px'}}>
                                             <Tab.Pane eventKey="insurance">
                                                <ReferralInsuranceTab 
                                                    referral={referral} 
                                                    selected={this.state.selectedTabs[referral.id] === "insurance"} 
                                                     onReferralChange={this.handleReferralChange}
                                                     readOnly={locked}
                                                />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="validity">
                                                <ReferralValidityTab
                                                    referral={referral}
                                                    selected={this.state.selectedTabs[referral.id] === "validity"}
                                                    onReferralChange={this.handleReferralChange}
                                                    readOnly={locked}
                                                />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="clinicians">
                                                <ReferralCliniciansTab
                                                    referral={referral}
                                                    selected={this.state.selectedTabs[referral.id] === "clinicians"}
                                                    onReferralChange={this.handleReferralChange}
                                                    readOnly={locked}
                                                />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="dates">
                                                <ReferralDatesTab
                                                    referral={referral}
                                                    selected={this.state.selectedTabs[referral.id] === "dates"}
                                                    onReferralChange={this.handleReferralChange}
                                                    readOnly={locked}
                                                />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="automation">
                                                <ReferralObsXMLsTab
                                                    referral={referral} 
                                                    selected={this.state.selectedTabs[referral.id] === "automation"} 
                                                     onReferralChange={this.handleReferralChange}
                                                     readOnly={locked}
                                                />
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Col>
                                </Row>
                            </Tab.Container>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
        )
    }

    expansionTemplate = (referral) => {
        const locked = referral.isLocked;
        return (
            <ReferralItemModal
                ref={ref => { this.referralItemModalRefs[referral.id] = ref; }}
                referral={referral}
                readOnly={locked}
                onItemsChange={(items) => this.handleReferralChange(referral.id, 'items', items)}
                onReferralChange={this.handleReferralChange}
            />
        );
    }

    actionsTemplate = (referral) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Dropdown>
                <Dropdown.Toggle as={DotsToggle} />
                <Dropdown.Menu>
                    <Dropdown.Item
                        disabled={referral.isLocked}
                        onClick={() => this.updateReferral(referral)}
                    >
                        <FontAwesomeIcon icon={["fas", "edit"]} className="mr-1" /> Update
                    </Dropdown.Item>
                    <Dropdown.Item
                        disabled={![
                            InsuranceReferralStatus.APPROVED.key,
                            InsuranceReferralStatus.COMPLETED.key,
                            InsuranceReferralStatus.EXPIRED.key,
                        ].includes(referral.status)}
                        onClick={() => this.handleEdit(referral)}
                    >
                        <FontAwesomeIcon icon={["fas", "wrench"]} className="mr-1" /> Edit
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )

    renderCreateReferralModal = () => {
        return (
            <Modal visible={this.state.createReferralModalVisible}
                   header={"Create Referral"}
                   closeAction={() => this.setState({createReferralModalVisible: false})}>
                <CreateReferralModal
                    onClose = {() => {
                        this.retrieveReferrals();
                        this.setState({createReferralModalVisible: false});
                    }}
                />
            </Modal>
        )
    }

    openCreateReferralModal = () => {
        this.setState({createReferralModalVisible: true})
    }

    // Text filters fire on every keystroke; debounce the backend fetch so we
    // don't send a request per character.
    debouncedRetrieve = () => {
        if (this.filterDebounce) {
            clearTimeout(this.filterDebounce);
        }
        this.filterDebounce = setTimeout(() => {
            this.retrieveReferrals();
        }, 400);
    }

    handleFilterChange = (field, value) => {
        const isTextFilter = field === 'patientName' || field === 'patientMrn';
        this.setState({
            filters: {
                ...this.state.filters,
                [field]: value
            },
            page: 1
        }, () => {
            if (isTextFilter) {
                this.debouncedRetrieve();
            } else {
                this.retrieveReferrals();
            }
        });
    }

    render() {
        const { page, rows, totalCount } = this.state;
        const first = (page - 1) * rows;

        return (
            <>
                <div className="preapproval-container">
                    <div style={{padding: '10px'}} className="data-table-header-wrapper">
                        <div className="float-left">
                            <PageTitle title="Pre Approval" className="bg-white"/>
                        </div>
                        <div className="float-right" style={{display: "flex", alignItems: "center", gap: "15px"}}>
                            <PrimeDateRangeInput
                                value={this.state.dateRange}
                                onChange={this.handleDateRangeChange}
                            />
                            <Button variant="success"
                                    onClick={() => this.openCreateReferralModal()}>
                                Create Referral
                            </Button>
                        </div>
                    </div>

                    <DataTable
                        value={this.state.referrals}
                        dataKey="id"
                        className="inline-tabs-referrals-table"
                        emptyMessage="No referrals found"
                        expandedRows={this.state.expandedReferrals}
                        onRowToggle={(e) => this.setState({expandedReferrals: e.data})}
                        rowExpansionTemplate={(referral) => this.expansionTemplate(referral)}
                        paginator
                        lazy
                        rows={rows}
                        first={first}
                        totalRecords={totalCount}
                        rowsPerPageOptions={[10, 25, 50, 100, 200]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                        onPage={(event) => {
                            const newPage = (event.page ?? 0) + 1; // PrimeReact page is 0-based; API expects 1-based
                            this.setState({ page: newPage, rows: event.rows }, () => {
                                this.retrieveReferrals();
                            });
                        }}
                        rowClassName={(referral) => ({
                            'row-status-approved': referral.status === 'APPROVED',
                            'row-status-requested': referral.status === 'REQUESTED',
                            'row-status-completed': referral.status === 'COMPLETED' || referral.status === 'EXPIRED',
                        })}
                    >
                        <Column expander headerStyle={{width: '3%', minWidth: '15px'}} />
                        <Column header="Clinic" body={this.clinicColumnTemplate} headerStyle={{width: '9%', minWidth: '140px'}} />
                        <Column header="Visit Date" body={this.visitDateTemplate} headerStyle={{width: '7%', minWidth: '100px', textAlign: 'center'}} style={{textAlign: 'center'}} />
                        <Column header="Cycle" body={this.cycleColumnTemplate} headerStyle={{width: '10%', minWidth: '130px'}}
                            filter filterElement={
                                <Select isClearable
                                    options={ReferralCycleEnum.toArray().map(i => ({ value: i.key, label: i.name }))}
                                    value={this.state.filters.cycle ? { value: this.state.filters.cycle, label: ReferralCycleEnum[this.state.filters.cycle]?.name } : null}
                                    onChange={(opt) => this.handleFilterChange('cycle', opt ? opt.value : '')}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                />
                            }
                        />
                        <Column header="Referral Type" body={this.referralTypeTemplate} headerStyle={{width: '6%', minWidth: '80px'}}
                            filter filterElement={
                                <Select isClearable
                                    options={DirectBillingReferralTypeEnum.toArray().map(i => ({ value: i.key, label: i.name }))}
                                    value={this.state.filters.referralType ? { value: this.state.filters.referralType, label: DirectBillingReferralTypeEnum[this.state.filters.referralType]?.name } : null}
                                    onChange={(opt) => this.handleFilterChange('referralType', opt ? opt.value : '')}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                />
                            }
                        />
                        <Column header="Modality" body={this.modalityColumnTemplate} headerStyle={{width: '10%', minWidth: '140px'}}
                            filter filterElement={
                                <Select isClearable
                                    options={EnumDirectBillingType.radiologyExternalReferralType.map(i => ({ value: i.key, label: i.name }))}
                                    value={this.state.filters.modality ? { value: this.state.filters.modality, label: EnumDirectBillingType.radiologyExternalReferralType.find(i => i.key === this.state.filters.modality)?.name } : null}
                                    onChange={(opt) => this.handleFilterChange('modality', opt ? opt.value : '')}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                />
                            }
                        />
                        <Column header="MRN" body={this.mrnTemplate} headerStyle={{width: '6%', textAlign: 'center'}} style={{width: '6%', textAlign: 'center'}}
                            filter filterElement={
                                <input type="text" style={{ width: '100%', fontSize: '11px', padding: '2px 6px', height: '28px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                    value={this.state.filters.patientMrn}
                                    onChange={(e) => this.handleFilterChange('patientMrn', e.target.value)}
                                    placeholder="MRN" />
                            }
                        />
                        <Column header="Patient Name" body={this.patientTemplate} headerStyle={{width: '8%', minWidth: '130px', textAlign: 'center'}} style={{textAlign: 'center'}}
                            filter filterElement={
                                <input type="text" style={{ width: '100%', fontSize: '11px', padding: '2px 6px', height: '28px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                    value={this.state.filters.patientName}
                                    onChange={(e) => this.handleFilterChange('patientName', e.target.value)}
                                    placeholder="Patient Name" />
                            }
                        />
                        <Column header="ICD Code" body={this.icdTemplate} headerStyle={{width: '6%', minWidth: '80'}} />
                        <Column header="Status" body={(referral) => this.statusTemplate(referral)} headerStyle={{width: '10%', minWidth: '140px'}}
                            filter filterElement={
                                <Select isClearable
                                    options={InsuranceReferralStatus.toArray().map(i => ({ value: i.key, label: i.name }))}
                                    value={this.state.filters.status ? { value: this.state.filters.status, label: InsuranceReferralStatus[this.state.filters.status]?.name } : null}
                                    onChange={(opt) => this.handleFilterChange('status', opt ? opt.value : '')}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{ control: (b) => ({ ...b, minHeight: '28px', height: '28px', fontSize: '11px' }), valueContainer: (b) => ({ ...b, height: '28px', padding: '0 6px' }), input: (b) => ({ ...b, margin: 0, padding: 0 }), indicatorsContainer: (b) => ({ ...b, height: '28px' }), option: (b) => ({ ...b, fontSize: '11px', padding: '4px 6px' }) }}
                                />
                            }
                        />
                        <Column header="Details" body={this.detailsTemplate} headerStyle={{width: '35%', minWidth: '580px'}} />
                        <Column header="Actions" body={this.actionsTemplate} headerStyle={{width: '3%', minWidth: '50px'}} />
                    </DataTable>
                    <InsuranceDocumentLegend/>
                </div>
                {this.renderCreateReferralModal()}
            </>
        )
    }
}

const DotsToggle = forwardRef(({ onClick }, ref) => (
    <span
        ref={ref}
        onClick={(e) => { e.preventDefault(); onClick(e); }}
        style={{ cursor: 'pointer', color: '#6c757d', fontSize: '16px', padding: '0 4px' }}
    >
        <FontAwesomeIcon icon={["fas", "ellipsis-v"]} />
    </span>
));

function DiagnosisCell({ referral, onDiagnosisChange }) {
    const [selectorKey, setSelectorKey] = useState(0);

    const items = referral.diagnosisGroup?.diagnosisItems || [];
    const locked = referral.isLocked;

    const handleSelect = (selected) => {
        if (!selected) return;
        const newItem = {
            code: selected.code,
            description: selected.description,
            level: items.some(i => i.level === 'PRINCIPAL') ? 'SECONDARY' : 'PRINCIPAL',
            type: 'FINAL',
            provider: null,
        };
        onDiagnosisChange([...items, newItem]);
        // Reset the selector so it clears and is ready for the next code.
        setSelectorKey((k) => k + 1);
    };

    const handleRemove = (index) => {
        // Removing the principal leaves none; the next code added becomes the principal.
        onDiagnosisChange(items.filter((_, i) => i !== index));
    };

    return (
        <div className="icd-cell">
            {items.map((item, index) => {
                const prefix = item.level === 'PRINCIPAL' ? 'P: ' : 'S: ';
                return (
                    <div key={`dg-${index}`} style={{ position: 'relative', display: 'inline-block', marginBottom: '2px' }}>
                        <HoverableDescriptionBox
                            header={`${prefix}${item.code}`}
                            description={item.description || item.code}
                            className="icd-box"
                        />
                        {!locked && (
                            <button
                                onClick={() => handleRemove(index)}
                                style={{
                                    position: 'absolute',
                                    top: '-4px',
                                    right: '-4px',
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: '#dc3545',
                                    color: '#fff',
                                    fontSize: '9px',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1,
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                );
            })}
            {!locked && (
                <div style={{ marginTop: '4px' }}>
                    <IcdCodeSelector
                        key={selectorKey}
                        icd={null}
                        onIcdSelected={handleSelect}
                    />
                </div>
            )}
        </div>
    );
}

